import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { VoiceAssistant } from "@/components/VoiceAssistant";
import { OfflineBanner } from "@/components/OfflineBanner";
import { ChatUnreadProvider, useChatUnread } from "@/context/ChatUnreadContext";
import { NotificationProvider, useNotifications } from "@/context/NotificationContext";
import { useTheme } from "@/context/ThemeContext";
import {
  Menu, LogOut, X, AlertTriangle, Activity, Sun, Moon, Search, Plus, Bell,
  PanelLeftClose, PanelLeftOpen, ChevronRight, Building2, Contact, Target, ListTodo, FileText, Hash, StickyNote, Settings as SettingsIcon,
  AtSign, UserCheck, CheckCheck,
} from "lucide-react";
import { navSections, allNavItems, quickAccess, resolveBreadcrumbs } from "@/components/layout/navConfig";
import { NavLink, FavoritesSection } from "@/components/layout/NavSections";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavFavorites } from "@/components/layout/useNavFavorites";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { GlobalEnrichBadge } from "@/components/GlobalEnrichBadge";
import { useMailboxBadge, MailboxNavBadge } from "@/components/mailbox/useMailboxBadge";

const ROLE_LABELS = { admin: "Administrator", manager: "Manager", member: "Mitarbeiter", readonly: "Nur Lesen" };

const QUICK_CREATE = [
  { label: "Kunde", icon: Building2, to: "/customers?new=1" },
  { label: "Kontakt", icon: Contact, to: "/customers?new=1" },
  { label: "Lead", icon: Target, to: "/praxissuche" },
  { label: "Aufgabe", icon: ListTodo, to: "/tasks?new=1" },
  { label: "Servicebericht", icon: FileText, to: "/service-reports/new" },
  { label: "Seriennummer", icon: Hash, to: "/serial-numbers?new=1" },
];

const openCommandPalette = () => window.dispatchEvent(new Event("open-command-palette"));

const LayoutInner = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useChatUnread();
  const mailboxBadge = useMailboxBadge(!!user);
  const { notifications, unreadCount: notifUnread, markAllRead, markOneRead } = useNotifications();
  const { theme, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { const v = localStorage.getItem("sidebar-collapsed"); return v === null ? true : v === "true"; } catch { return true; }
  });
  const { favorites, favoriteItems, toggleFavorite, handleFavDragEnd } = useNavFavorites(allNavItems);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("sidebar-collapsed", String(next)); } catch (e) { console.debug("Sidebar-Status:", e); }
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navLinkProps = {
    location, favorites, unreadCount, mailboxBadge,
    onToggleFavorite: toggleFavorite,
    onNavigate: () => setMobileOpen(false),
  };

  const breadcrumbs = resolveBreadcrumbs(location.pathname);
  const initials = user?.full_name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "U";
  const roleLabel = ROLE_LABELS[user?.role] || "Benutzer";

  const isActive = (item) =>
    item.match ? item.match(location.pathname)
      : location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));

  // Icon-only rail (collapsed desktop sidebar, Doctolib-Stil, navy)
  const CollapsedNav = () => (
    <TooltipProvider delayDuration={150}>
      <nav className="flex-1 min-h-0 py-3 px-2 space-y-1 overflow-y-auto" data-testid="sidebar-collapsed-nav">
        {allNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <Link
                  to={item.path}
                  aria-label={item.label}
                  data-testid={`nav-collapsed-${item.path.replace("/", "") || "dashboard"}`}
                  className={`relative flex items-center justify-center h-11 w-11 mx-auto rounded-lg transition-colors ${
                    active ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  {active && <span className="absolute -left-2 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-[#E8700A]" />}
                  <Icon className="w-5 h-5" strokeWidth={1.85} />
                  {item.path === "/mailbox" && <MailboxNavBadge badge={mailboxBadge} compact />}
                  {item.path === "/chat" && unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-[#E8700A] text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-medium">{item.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </nav>
    </TooltipProvider>
  );

  const ExpandedNav = ({ mobile = false }) => (
    <nav className="flex-1 min-h-0 p-4 space-y-4 overflow-y-auto">
      {!mobile && (
        <FavoritesSection favorites={favorites} favoriteItems={favoriteItems} onDragEnd={handleFavDragEnd} navLinkProps={navLinkProps} />
      )}
      {navSections.map((section, si) => (
        <div key={section.label || `sec-${si}`} className="space-y-1">
          {section.label && (
            <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{section.label}</p>
          )}
          {section.items.map((item) => (
            <NavLink key={item.path} item={item} mobile={mobile} {...navLinkProps} />
          ))}
        </div>
      ))}
    </nav>
  );

  const UserBlock = ({ mobile = false }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2.5 px-2" data-testid="user-menu-btn">
          <Avatar className="w-9 h-9 shrink-0">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col items-start text-left min-w-0">
            <span className="text-sm font-medium truncate max-w-[150px]">{user?.full_name}</span>
            <span className="text-xs text-muted-foreground">{roleLabel}</span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={mobile ? "center" : "start"} side={mobile ? "top" : "right"} className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user?.full_name}</span>
            <span className="text-xs text-muted-foreground">{user?.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-link">
          <SettingsIcon className="w-4 h-4 mr-2" /> Einstellungen
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggleTheme} data-testid="theme-toggle-desktop">
          {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
          {theme === "dark" ? "Hellmodus" : "Dunkelmodus"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/admin/errors")} data-testid="errors-link">
          <AlertTriangle className="w-4 h-4 mr-2" /> Fehler-Protokoll
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/diagnose")} data-testid="diagnose-link">
          <Activity className="w-4 h-4 mr-2" /> System-Diagnose
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const QuickCreateMenu = ({ testid = "quick-create-btn" }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid={testid}>
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Neu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>Neu anlegen</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {QUICK_CREATE.map((q) => (
          <DropdownMenuItem key={q.label} onClick={() => navigate(q.to)} data-testid={`quick-create-${q.label.toLowerCase()}`}>
            <q.icon className="w-4 h-4 mr-2 text-muted-foreground" /> {q.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const NotificationsMenu = () => {
    const totalUnread = (unreadCount || 0) + (notifUnread || 0);
    const fmtWhen = (s) => {
      if (!s) return "";
      const d = new Date(s);
      return isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    };
    const iconFor = (t) => (t === "mention" ? AtSign : t === "assignment" ? UserCheck : Bell);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn" aria-label="Benachrichtigungen">
            <Bell className="w-5 h-5" />
            {totalUnread > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-destructive text-white">
                {totalUnread > 9 ? "9+" : totalUnread}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80">
          <div className="flex items-center justify-between px-2 py-1.5">
            <DropdownMenuLabel className="p-0">Benachrichtigungen</DropdownMenuLabel>
            {notifUnread > 0 && (
              <button onClick={markAllRead} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="notif-mark-all-read">
                <CheckCheck className="w-3.5 h-3.5" />Alle gelesen
              </button>
            )}
          </div>
          <DropdownMenuSeparator />
          <div className="max-h-80 overflow-y-auto">
            {unreadCount > 0 && (
              <DropdownMenuItem onClick={() => navigate("/chat")} data-testid="notif-chat">
                <MessageSquareIcon /> {unreadCount} neue Chat-Nachricht{unreadCount > 1 ? "en" : ""}
              </DropdownMenuItem>
            )}
            {notifications.length > 0 ? (
              notifications.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <DropdownMenuItem
                    key={n.id}
                    className={`items-start gap-2 ${n.read ? "opacity-60" : ""}`}
                    onClick={() => { markOneRead(n.id); if (n.url) navigate(n.url); }}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium truncate">{n.title}</span>
                      <span className="block text-xs text-muted-foreground line-clamp-2">{n.body}</span>
                      <span className="block text-[10px] text-muted-foreground/70 mt-0.5">{fmtWhen(n.created_at)}</span>
                    </span>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                  </DropdownMenuItem>
                );
              })
            ) : (
              unreadCount === 0 && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="notif-empty">
                  Keine neuen Benachrichtigungen
                </div>
              )
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const sidebarWidth = collapsed ? "lg:w-[72px]" : "lg:w-64";
  const contentPad = collapsed ? "lg:pl-[72px]" : "lg:pl-64";

  return (
    <div className="min-h-screen bg-background">
      <CommandPalette />
      <OnboardingDialog />

      {/* Desktop Sidebar */}
      <aside className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col transition-[width] duration-300 ease-in-out ${sidebarWidth}`}>
        <div className={`flex flex-col flex-1 min-h-0 border-r ${collapsed ? "bg-[#1A3A5C] border-[#102a44]" : "bg-card border-border"}`}>
          <div className={`flex items-center h-16 shrink-0 border-b ${collapsed ? "justify-center px-2 border-white/10" : "justify-between px-4 border-border"}`}>
            {!collapsed && (
              <Link to="/" className="flex items-center gap-3 min-w-0">
                <img src="/assets/rnetwork-logo.webp" alt="R-Network" className="h-8" />
              </Link>
            )}
            <Button variant="ghost" size="icon" onClick={toggleCollapsed} data-testid="sidebar-collapse-toggle"
              aria-label={collapsed ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
              className={collapsed ? "text-white/80 hover:text-white hover:bg-white/10" : ""}>
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </Button>
          </div>

          {collapsed ? <CollapsedNav /> : <ExpandedNav />}

          <div className={`border-t shrink-0 ${collapsed ? "p-2 border-white/10" : "p-3 border-border"}`}>
            {collapsed ? (
              <Button variant="ghost" size="icon" className="w-11 h-11 mx-auto text-white/80 hover:text-white hover:bg-white/10" onClick={() => navigate("/settings")} title={`${user?.full_name} · ${roleLabel}`} data-testid="user-menu-btn-collapsed">
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-[#E8700A] text-white text-xs font-semibold">{initials}</AvatarFallback></Avatar>
              </Button>
            ) : (
              <UserBlock />
            )}
          </div>
        </div>
      </aside>

      {/* Desktop Top Header */}
      <header className={`hidden lg:flex fixed top-0 right-0 z-40 h-16 items-center justify-between px-6 bg-background/80 backdrop-blur-md border-b border-border transition-[left] duration-300 ease-in-out ${collapsed ? "left-[72px]" : "left-64"}`}>
        <div className="flex items-center gap-1 min-w-0">
          <nav className="flex items-center gap-1" aria-label="Schnellzugriff">
            {quickAccess.map((q) => {
              const active = q.match(location.pathname);
              const Icon = q.icon;
              return (
                <Link
                  key={q.path}
                  to={q.path}
                  data-testid={`quick-access-${q.path.replace("/", "") || "dashboard"}`}
                  className={`relative flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium transition-colors ${
                    active ? "text-[#1A3A5C] bg-[#EBF1F7]" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.9} />
                  <span className="hidden xl:inline">{q.label}</span>
                  {active && <span className="absolute -bottom-[1px] left-2 right-2 h-0.5 rounded-full bg-[#E8700A]" />}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          <GlobalEnrichBadge />
          <Button variant="outline" size="sm" onClick={openCommandPalette} data-testid="global-search-btn"
            className="gap-2 text-muted-foreground font-normal w-56 justify-start">
            <Search className="w-4 h-4" /> Suchen…
            <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-border bg-muted">⌘K</kbd>
          </Button>
          <QuickCreateMenu />
          <NotificationsMenu />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="header-profile-btn" aria-label="Profil">
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback></Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{user?.full_name}</span>
                  <span className="text-xs text-muted-foreground">{roleLabel} · {user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}><SettingsIcon className="w-4 h-4 mr-2" /> Einstellungen</DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === "dark" ? "Hellmodus" : "Dunkelmodus"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="w-4 h-4 mr-2" /> Abmelden</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Breadcrumb Strip */}
      <div className={`hidden lg:flex fixed top-16 right-0 z-30 h-9 items-center px-6 bg-background border-b border-border/70 transition-[left] duration-300 ease-in-out ${collapsed ? "left-[72px]" : "left-64"}`}>
        <nav className="flex items-center gap-1.5 text-xs min-w-0" aria-label="Breadcrumb" data-testid="breadcrumbs">
          {breadcrumbs.map((c, i) => (
            <span key={`${c.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />}
              {c.to ? (
                <Link to={c.to} className="text-muted-foreground hover:text-foreground transition-colors truncate">{c.label}</Link>
              ) : (
                <span className="font-semibold text-foreground truncate" data-testid="breadcrumb-current">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      </div>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-16 bg-card/95 backdrop-blur border-b border-border">
        <div className="flex items-center justify-between h-full px-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/assets/rnetwork-logo.webp" alt="R-Network" className="h-7" />
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={openCommandPalette} data-testid="global-search-btn-mobile" aria-label="Suchen">
                <Search className="w-5 h-5" />
              </Button>
              <QuickCreateMenu testid="quick-create-btn-mobile" />
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
            </div>
            <SheetContent side="left" className="w-80 p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between h-16 px-6 border-b border-border">
                  <SheetTitle className="font-semibold">Menü</SheetTitle>
                  <SheetDescription className="sr-only">Hauptnavigation und Kontooptionen</SheetDescription>
                  <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></Button>
                </div>
                <ExpandedNav mobile />
                <div className="p-4 border-t border-border mb-16 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10"><AvatarFallback className="bg-primary text-primary-foreground">{initials}</AvatarFallback></Avatar>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{user?.full_name}</p>
                      <p className="text-xs text-muted-foreground">{roleLabel}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full h-11" onClick={toggleTheme} data-testid="theme-toggle-sheet">
                    {theme === "dark" ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
                    {theme === "dark" ? "Hellmodus" : "Dunkelmodus"}
                  </Button>
                  <Button variant="destructive" className="w-full h-11" onClick={handleLogout} data-testid="mobile-logout-btn">
                    <LogOut className="w-5 h-5 mr-2" /> Abmelden
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className={`${contentPad} pt-16 lg:pt-[100px] min-h-screen pb-24 lg:pb-8 transition-[padding] duration-300 ease-in-out`}>
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</div>
      </main>

      <MobileBottomNav onMenuClick={() => setMobileOpen(true)} unreadCount={unreadCount} />
      <OfflineBanner />
      <VoiceAssistant />
    </div>
  );
};

// kleine Hilfs-Icon-Komponente für Benachrichtigungen (vermeidet zusätzlichen Import-Namen)
const MessageSquareIcon = () => (
  <span className="w-4 h-4 mr-2 inline-flex items-center justify-center text-muted-foreground">
    <StickyNote className="w-4 h-4" />
  </span>
);

export const Layout = ({ children }) => (
  <ChatUnreadProvider>
    <NotificationProvider>
      <LayoutInner>{children}</LayoutInner>
    </NotificationProvider>
  </ChatUnreadProvider>
);

export default Layout;
